 
import { Box, Typography, useTheme } from '@mui/joy';
import { DataKey } from '../../lib/model';
import {
	DOLLAR_FORMATTER,
	PERCENT_FORMATTER,
	SM_PERCENT_FORMATTER
} from '../../lib/format';
import { useRoute } from '../../providers/RouteProvider';

interface IProps {
	titleText: string;
	d: { [x: string]: string | number };
	topic: string;
	isFiltered?: boolean;
	donor?: boolean;
	isAnalysis?: boolean;
}

const ChartTooltip = ({
	topic,
	titleText,
	d,
	isFiltered,
	donor = false,
	isAnalysis = false
}: IProps) => {
	const theme = useTheme();

	const { donorTab } = useRoute();
	const labels: { [x: string]: string } = {
		WomenGOP: 'Republican Women',
		MenGOP: 'Republican Men',
		WomenIND: 'Independent Women',
		MenIND: 'Independent Men',
		WomenDEM: 'Democratic Women',
		MenDEM: 'Democratic Men',
		DEM: 'Democratic',
		GOP: 'Republican',
		IND: 'Independent'
	};

	const color: { [x: string]: string } = {
		WomenGOP:
			theme.colorSchemes.light.palette.categoryPartyRepublicanWomen.solidBg,
		MenGOP: theme.colorSchemes.light.palette.categoryPartyRepublicanMen.solidBg,
		WomenIND:
			theme.colorSchemes.light.palette.categoryPartyIndependentWomen.solidBg,
		MenIND:
			theme.colorSchemes.light.palette.categoryPartyIndependentMen.solidBg,
		WomenDEM:
			theme.colorSchemes.light.palette.categoryPartyDemocratWomen.solidBg,
		MenDEM: theme.colorSchemes.light.palette.categoryPartyDemocratMen.solidBg
	};
	const candidate = +d.numCand > 1 ? 'candidates' : 'candidate';

	const donorType = {
		[DataKey.DonorWomen]: 'Women',
		[DataKey.DonorMen]: 'Men',
		[DataKey.DonorCollegeEd]: 'College educated',
		[DataKey.DonorNonCollegeEd]: 'Non-college educated',
		[DataKey.DonorAsian]: 'Asian',
		[DataKey.DonorBlack]: 'Black',
		[DataKey.DonorLatino]: 'Latino',
		[DataKey.DonorWhite]: 'White',
		[DataKey.DonorMarried]: 'Married',
		[DataKey.DonorUnmarried]: 'Unmarried',
		[DataKey.DonorDem]: 'Democratic',
		[DataKey.DonorRep]: 'Republican',
		[DataKey.DonorInd]: 'Other',
		[DataKey.DonorUnder40]: 'Donors under age 40',
		[DataKey.Donor40_64]: 'Donors aged 40-64',
		[DataKey.Donor65Plus]: 'Donors aged 65+'
	};

	const formatValue = (value: number) => {
		if (+value < 0.01 && +value > 0) {
			if (SM_PERCENT_FORMATTER(value) === '0.00%') return '0%';
			else return SM_PERCENT_FORMATTER(value);
		} else return PERCENT_FORMATTER(value);
	};
	const ageKeys = [DataKey.DonorUnder40, DataKey.Donor40_64, DataKey.Donor65Plus] as string[];
	const isAgeKey = ageKeys.includes(d.chartCol as string);
	const phrase = {
		[DataKey.TotalRaised]: `The average total raised in this group among the <strong>${
			d.numCand
		} ${candidate} </strong> is <strong>${DOLLAR_FORMATTER(
			+d.value
		)}</strong>.`,
		[DataKey.PropSelf]: `Average proportion of self-financing among the <strong>${
			d.numCand
		} ${candidate}</strong> is <strong>${formatValue(
			isFiltered ? +d.value : +d.percent
		)}</strong>.`,
		[DataKey.PropSmall]: `Average proportion of contributions $200 or less among the <strong>${
			d.numCand
		} ${candidate}</strong> is <strong>${formatValue(
			isFiltered ? +d.value : +d.percent
		)}</strong>.`,
		donorMoney: `${donorType[d.chartCol]}${isAgeKey ? '' : ' donors'} accounted for <strong>${(
			+d.value * 100
		).toFixed()}% of the total contributions.</strong>`,
		donorContrib: `${donorType[d.chartCol]}${isAgeKey ? '' : ' donors'} were <strong>${(
			+d.value * 100
		).toFixed()}% of contributors.</strong>`
	};
	const getColor: (arg0: string, arg1: string) => string = (
		partyLabel,
		label
	) => {
		const prefix = label.includes('Women') ? 'Women' : 'Men';
		const name = `${prefix}${partyLabel}`;
		return color[name];
	};
	const labelsToExpand = ['MENA Women', 'NatAm Women', 'AAPI Women'];
	const expanded: { [x: string]: string } = {
		'MENA Women': 'Middle Eastern/North African Women',
		'NatAm Women': 'Native American/Alaska Native/Native Hawaiian Women',
		'AAPI Women': 'Asian American/Pacific Islander Women'
	};

	const inBetween = isAnalysis ? '' : ' — '; //no title wanted for Analysis page disagg charts (passing in empty string from these charts)
	const header = !donor
		? isFiltered
			? `${labels[d.partyLabel]}${inBetween}${titleText} — ${
					labelsToExpand.includes(d.label as string)
						? expanded[d.label]
						: d.label
			  }`
			: `${labels[d.group]} — ${titleText}`
		: donor && isFiltered
		? `${d.label} — ${titleText} — ${
				labelsToExpand.includes(d.group as string) ? expanded[d.group] : d.group
		  }`
		: `${d.label} — ${titleText}`;

	const isMoney = donor && donorTab === 0;
	const isContrib = donor && donorTab === 1;
	return (
		<Box sx={{ p: '1rem .5rem', maxWidth: '350px' }}>
			<Typography
				sx={{
					color: donor
						? d.color
						: isFiltered
						? getColor(d.partyLabel as string, d.label as string)
						: color[d.group],
					fontSize: '20px',
					fontWeight: 700,
					pb: '.5rem',
					borderBottom: '1px solid black'
				}}
			>
				{header}
			</Typography>
			<Box
				sx={{ mt: '.5rem', fontSize: '16px' }}
				dangerouslySetInnerHTML={{
					__html:
						phrase[isMoney ? 'donorMoney' : isContrib ? 'donorContrib' : topic]
				}}
			></Box>
		</Box>
	);
};

export default ChartTooltip;
